// @docs:
//   - docs/wip/CREW_PAPER_REQUESTS.md  § Skipper tracker, checks, downloads (Phase 3) — validity checks
// ==============================================
// The crew side of the validity check (step 3.2), the same rule the API uses
// (paperRequestsService.paperCheck): a passport must be valid until the date
// the skipper asked for (passportValidUntil); anything else, and a passport
// when no date was asked, until arrival. It flags, never blocks — the skipper
// decides — so the crew member sees it while they can still fix it.
// ==============================================

import type { CrewRequest } from './types';
import { firstName, isDate } from './dates';

const MONTH = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** '2027-01-26' → '26 Jan 2027' (the passport artboard's date) */
export const fmtLong = (d: string) => `${Number(d.slice(8, 10))} ${MONTH[Number(d.slice(5, 7)) - 1]} ${d.slice(0, 4)}`;

export interface Validity { ok: boolean; text: string }

/**
 * "Valid past 26 Jan 2027, as Tony needs" (green) or the problem (red) for a paper with this
 * expiry; null when there is nothing to say (no expiry, or no date to measure it against).
 */
export function validityFor(type: string, expiry: string | null | undefined, req: Pick<CrewRequest, 'passportValidUntil' | 'arriveOn' | 'requesterName'>): Validity | null {
  if (!isDate(expiry)) return null;
  const who = firstName(req.requesterName);
  if (type === 'passport' && isDate(req.passportValidUntil)) {
    return expiry >= req.passportValidUntil
      ? { ok: true, text: `Valid past ${fmtLong(req.passportValidUntil)}, as ${who} needs` }
      : { ok: false, text: `Expires ${fmtLong(expiry)} — ${who} needs it valid until at least ${fmtLong(req.passportValidUntil)}` };
  }
  if (!isDate(req.arriveOn)) return null;
  return expiry >= req.arriveOn
    ? (type === 'passport' ? { ok: true, text: `Valid past arrival on ${fmtLong(req.arriveOn)}` } : null)
    : { ok: false, text: `Ends ${fmtLong(expiry)}, before arrival on ${fmtLong(req.arriveOn)} — ${who} will ask about it` };
}
